import React from 'react';
import { z } from 'zod';

import { useBlockName } from '@wp-block-builder/editor/hooks';
import { type TBlock } from '@wp-block-builder/package';
import { ColumnBlockLayout, useIsLoopIteration } from '@wp-block-builder/stores/BlockLayoutStore';
import ColumnPlaceholder from '@wp-block-builder/views/EditableMessage/components/AddBlockButton/ColumnPlaceholder';

import defineBlock from '../helpers/defineBlock';
import { BlockSchema } from '../types';

import { EditableBlockLayout, ReadOnlyBlockLayout } from './BlockLayout';

export const ColumnsSectionBlockSchema = defineBlock(
  'ColumnsSection',
  z.object({
    dimensionsRule: z.enum(['percentage', 'manual']).nullish(),
    fixedWidths: z.tuple([z.number().nullable(), z.number().nullable(), z.number().nullable()]).nullish(),
    columnGridDimensions: z.union([z.tuple([z.number(), z.number()]), z.tuple([z.number()])]).nullish(),
    contentAlignment: z.enum(['top', 'middle', 'bottom']).nullish(),
    columnsCount: z.union([z.literal(2), z.literal(3)]).nullish(),
    columnGap: z.number().nullish(),
    mobileLayout: z.enum(['columns', 'stacked']).nullish(),
    get blocks() {
      return z.array(z.nullable(BlockSchema)).nullish();
    },
  })
);
export type TColumnsSectionBlock = z.infer<typeof ColumnsSectionBlockSchema>;

type ReadOnlyBlockProps = {
  block: TColumnsSectionBlock;
  BlockComponent: (props: { block: TBlock }) => JSX.Element;
};
export function ReadOnlyBlock({ block, BlockComponent }: ReadOnlyBlockProps) {
  return (
    <ReadOnlyBlockLayout
      block={block}
      renderChildBlock={(childBlock) => {
        if (childBlock === null) {
          return <div style={{ height: '48px', width: '100%' }} />;
        }
        return <BlockComponent block={childBlock} />;
      }}
    />
  );
}

type EditableBlockProps = {
  block: TColumnsSectionBlock;
  BlockComponent: (props: { block: TBlock }) => JSX.Element;
};
export function EditableBlock({ block, BlockComponent }: EditableBlockProps) {
  const name = useBlockName(block.id);
  const isLoopIteration = useIsLoopIteration();
  return (
    <ColumnBlockLayout>
      <EditableBlockLayout
        block={block}
        renderChildBlock={(childBlock, columnIndex) => {
          if (childBlock === null) {
            if (isLoopIteration) {
              return <></>;
            }
            return <ColumnPlaceholder name={`${name}.props.blocks[${columnIndex}]`} />;
          }
          return <BlockComponent block={childBlock} />;
        }}
      />
    </ColumnBlockLayout>
  );
}
